// ============================================================
// api/federation.js
// Endpoint bindings for the mgmt/admin/* federation endpoints
// (Workstream 3, shipped May 2026 across 6 commits).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-tenants-federation-*.js
//   backend/func-carehub-api/src/functions/admin-clients-group-mappings-*.js
//   APIM operations: mgmt-admin-tenants-federation-*, mgmt-admin-clients-group-mappings-*
//
// Wire shape mirrored from W3 commit 5/6's handlers. All errors flow
// through ApiError per the shared client.js contract — 404 / 409 /
// 400 / 500 all surface as thrown ApiError instances with status,
// error (snake_case code), detail, and field (where applicable).
// ============================================================

import { apiGet, apiPost, apiPatch, apiDelete } from './client';

// ── TenantFederationConfig CRUD ─────────────────────────────

/**
 * Create a TenantFederationConfig row for the Tenant.
 *
 * Throws ApiError(404 tenant_not_found) if the Tenant doesn't exist.
 * Throws ApiError(409 federation_domain_already_configured) if another
 * active row has the same FederatedDomain (Gotcha #40 invariant).
 * Throws ApiError(409 federation_config_exists) if this Tenant already
 * has a config row.
 *
 * @param {number} tenantId
 * @param {Object} body
 * @param {string} body.externalTenantId      Client tenant GUID
 * @param {'m365'} body.identityProviderType  Only 'm365' supported today
 * @param {string} body.federatedDomain       Client email domain (lowercase)
 *
 * @returns {Promise<{
 *   tenantFederationConfigId: number,
 *   tenantId: number,
 *   externalTenantId: string,
 *   identityProviderType: 'm365',
 *   federatedDomain: string,
 *   consentGrantedAt: string|null,
 *   needsReconsent: boolean,
 *   isActive: boolean,
 *   createdAt: string|null,
 *   createdBy: string,
 * }>}
 */
export async function createTenantFederation(tenantId, body) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation`, body);
}

/**
 * Fetch the TenantFederationConfig row for the Tenant.
 *
 * Throws ApiError(404 federation_config_not_found) if no row exists —
 * UI callers may catch this and render the "not_configured" state
 * rather than treating it as an error.
 *
 * @param {number} tenantId
 * @returns {Promise<Object>} See createTenantFederation return shape.
 */
export async function getTenantFederation(tenantId) {
  return apiGet(`/mgmt/admin/tenants/${tenantId}/federation`);
}

/**
 * Patch the TenantFederationConfig row. Body fields are all optional;
 * at least one must be present.
 *
 * Post-W3.5: `federatedDomains` and the legacy `federatedDomain` field
 * are explicitly rejected by the backend (400 `use_domains_endpoints`)
 * — domain mutations go through addDomain / deleteDomain below.
 *
 * identityProviderType, consentGrantedAt, needsReconsent are immutable
 * from this endpoint (server returns ApiError(400 immutable_field)).
 *
 * @param {number} tenantId
 * @param {Object} partial
 * @param {string} [partial.externalTenantId]
 * @param {boolean} [partial.isActive]
 *
 * @returns {Promise<Object>} The updated row.
 */
export async function updateTenantFederation(tenantId, partial) {
  return apiPatch(`/mgmt/admin/tenants/${tenantId}/federation`, partial);
}

// ── TenantFederationDomains (1:N child) ─────────────────────

/**
 * Add a federated domain to an existing TenantFederationConfig.
 *
 * Throws ApiError(404 federation_config_not_found) if no config exists.
 * Throws ApiError(400 invalid_domain_format) on regex failure.
 * Throws ApiError(409 federated_domain_already_configured) when the
 * domain is already configured on any Tenant. The response body
 * carries `conflictingDomain` and `existingTenantId` for the UI to
 * surface in an inline error.
 *
 * @param {number} tenantId
 * @param {string} domain  Email domain string (lowercased server-side)
 *
 * @returns {Promise<{
 *   domainId: number,
 *   domain: string,
 *   tenantFederationConfigId: number,
 *   createdAt: string|null,
 *   createdBy: string,
 * }>}
 */
export async function addFederationDomain(tenantId, domain) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation/domains`, { domain });
}

/**
 * Remove a federated domain from a TenantFederationConfig.
 *
 * Throws ApiError(404 federation_config_not_found / federation_domain_not_found).
 * Throws ApiError(400 cannot_remove_last_domain) — at least one domain
 * must remain on every active config (runtime resolver requirement).
 *
 * @param {number} tenantId
 * @param {number} domainId  TenantFederationDomains.TenantFederationDomainID
 * @returns {Promise<null>}  204 No Content
 */
export async function deleteFederationDomain(tenantId, domainId) {
  return apiDelete(`/mgmt/admin/tenants/${tenantId}/federation/domains/${domainId}`);
}

// ── Consent URL generator ───────────────────────────────────

/**
 * Generate the consent URL for the client tenant admin to grant
 * admin consent for the Federation app reg. The URL is constructed
 * server-side from FEDERATION_APP_REG_CLIENT_ID + the Tenant's
 * ExternalTenantID + the Tenants.TenantID as state.
 *
 * Throws ApiError(404 federation_config_not_found) if no config row.
 * Throws ApiError(409 federation_config_inactive) if IsActive=0.
 *
 * @param {number} tenantId
 * @returns {Promise<{ consentUrl: string }>}
 */
export async function generateConsentUrl(tenantId) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation/consent-url`, {});
}

// ── Test Connection probe ───────────────────────────────────

/**
 * Probe federation Graph against the client tenant. Returns 200 in
 * all cases — the probe success/failure is in the response body.
 *
 * On detected consent revocation (Graph 403), the server flips
 * TenantFederationConfig.NeedsReconsent=1 inline AND emits a
 * consent_revoked AuditLog row. Callers should re-fetch the
 * TenantFederationConfig after a 'consent_revoked' result so the UI
 * picks up the new NeedsReconsent state.
 *
 * @param {number} tenantId
 * @returns {Promise<
 *   | { success: true, sampleGroupCount: number }
 *   | { success: false, error: 'consent_revoked'|'transient'|'unknown', message: string }
 * >}
 */
export async function testConnection(tenantId) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation/test-connection`, {});
}

/**
 * Pre-flight: verify whether the Workforce API SP has been provisioned
 * in the client tenant (CLAUDE.md Gotcha #51 — pure-resource-API SPs
 * are NOT auto-provisioned by any consent-URL flow; client-tenant GA
 * must run `az ad sp create --id <workforceApiAppId>` explicitly).
 *
 * Server reads /servicePrincipals?$filter=appId eq <id> against the
 * client tenant via the federation app-only Graph token. Returns 200
 * in all probe-failure cases — success/failure is in the body, same
 * pattern as testConnection.
 *
 * PURE READ — NEVER MUTATES federation lifecycle state. A 403 from
 * the verifier (most commonly "Application.Read.All not granted yet"
 * after we added the permission in 2026-05-25) is reported as
 * `permission_not_granted` and does NOT flip NeedsReconsent on the
 * TenantFederationConfig row. Consent-revoked semantics live in
 * test-connection / the runtime auth path, which probe actual
 * runtime permissions — not a fresh read-only diagnostic scope.
 *
 * Error codes:
 *   - permission_not_granted — Graph 403 + Authorization_RequestDenied
 *       OR "insufficient privileges" in body. Operator action:
 *       re-run step 2 (Federation Workload consent URL) as
 *       client-tenant GA so Application.Read.All is granted.
 *   - graph_forbidden — Graph 403 for any other reason (revoked
 *       consent, app removed, etc). Operator action: check the
 *       Federation Workload consent state in this tenant.
 *   - transient — Graph 5xx / network. Retry shortly.
 *   - unknown — anything else (logged to App Insights).
 *
 * spExists semantics:
 *   - true:  SP found in client tenant → onboarding step 1 complete.
 *   - false: SP not found, no error → operator must run `az ad sp
 *            create --id <workforceApiAppId>` as client-tenant GA.
 *   - null:  Could not determine (any of the error codes above).
 *
 * @param {number} tenantId
 * @returns {Promise<
 *   | { spExists: true,  workforceApiAppId: string, servicePrincipalId: string, displayName: string|null }
 *   | { spExists: false, workforceApiAppId: string, servicePrincipalId: null,    displayName: null }
 *   | { spExists: null,  workforceApiAppId: string, error: 'permission_not_granted'|'graph_forbidden'|'transient'|'unknown', message: string }
 * >}
 */
export async function verifyWorkforceApiSp(tenantId) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation/verify-workforce-api-sp`, {});
}

/**
 * Reset a tenant's federation consent state: ConsentGrantedAt -> NULL,
 * NeedsReconsent -> 1. Returns the tenant to "awaiting consent" so the
 * portal offers the consent URL again.
 *
 * Use after the client-side grant is invalidated: the enterprise app
 * deleted, the SP removed, the app registration rotated, or consent
 * revoked in the client tenant. Before this endpoint existed the fix
 * was a hand-written UPDATE against the live database.
 *
 * ⚠ THE CONFIRMATION IS MANDATORY AND SERVER-CHECKED. The caller must
 * pass the exact phrase `RESET CONSENT <tenantId>`; the server compares
 * it byte-for-byte (no trim, no case-fold) and 400s otherwise. It is
 * built here rather than typed by the caller so the two can never
 * disagree about the format — but the SERVER is the gate, not this.
 *
 * DOES NOT deactivate federation. IsActive is untouched: this says the
 * grant needs re-establishing, not "stop federating". The response
 * echoes isActive so the UI can show that federation was left alone.
 *
 * @param {number} tenantId
 * @param {string|null} [reason] Free text, recorded in the audit row.
 * @returns {Promise<{
 *   tenantFederationConfigId: number, tenantId: number,
 *   consentGrantedAt: null, needsReconsent: true,
 *   isActive: boolean, wasAlreadyReset: boolean,
 *   updatedAt: string|null, updatedBy: string
 * }>}
 */
export async function resetFederationConsent(tenantId, reason = null) {
  return apiPost(`/mgmt/admin/tenants/${tenantId}/federation/reset-consent`, {
    confirm: `RESET CONSENT ${tenantId}`,
    ...(reason ? { reason } : {}),
  });
}

// ── GroupMappings CRUD (Client-scoped) ──────────────────────

/**
 * List active GroupMappings for a Client. Soft-deleted rows are NOT
 * returned by this endpoint.
 *
 * @param {number} clientId
 * @returns {Promise<{
 *   groupMappings: Array<{
 *     groupMappingId: number,
 *     clientId: number,
 *     groupObjectId: string,
 *     groupDisplayName: string|null,
 *     role: 'manager'|'admin'|'carer',
 *     isActive: boolean,
 *     createdAt: string|null,
 *     createdBy: string,
 *   }>
 * }>}
 */
export async function listGroupMappings(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/group-mappings`);
}

/**
 * Create a GroupMapping for a Client.
 *
 * Role must be lowercase 'manager' | 'admin' | 'carer'. Auditor is
 * invitation-only — the server returns ApiError(400 invalid_role)
 * with allowedRoles in the body if 'auditor' is submitted.
 *
 * Throws ApiError(409 group_mapping_already_exists) on the
 * (clientId, groupObjectId, role) uniqueness check.
 *
 * @param {number} clientId
 * @param {Object} body
 * @param {string} body.groupObjectId         Client tenant security group GUID
 * @param {string} [body.groupDisplayName]    Cached for admin UI display
 * @param {'manager'|'admin'|'carer'} body.role
 *
 * @returns {Promise<Object>} See listGroupMappings row shape.
 */
export async function createGroupMapping(clientId, body) {
  return apiPost(`/mgmt/admin/clients/${clientId}/group-mappings`, body);
}

/**
 * Soft-delete a GroupMapping. Idempotent: re-deleting an already-
 * deactivated row returns 404, not 409.
 *
 * @param {number} clientId
 * @param {number} groupMappingId
 * @returns {Promise<null>} 204 No Content — body is null.
 */
export async function deleteGroupMapping(clientId, groupMappingId) {
  return apiDelete(`/mgmt/admin/clients/${clientId}/group-mappings/${groupMappingId}`);
}

// ── Available groups (federation picker, W4) ────────────────

/**
 * List M365 security groups in the federated client tenant that owns
 * the Client, marking each with whether it's already mapped to a
 * CareHub role on this Client. Powers the W4 group picker on the
 * GroupMappings create form.
 *
 * Auto-reveal contract: the four error codes below correspond to the
 * GUID-fallback auto-reveal in GroupMappingsTab.js. Each surfaces a
 * distinct copy line above the GUID input. Server-side, the
 * `federation_consent_lost` path also flips NeedsReconsent=1 on the
 * TenantFederationConfig row inline.
 *
 *   404 federation_not_configured     — no TenantFederationConfig row
 *   400 federation_not_active         — config exists, IsActive=0
 *   502 federation_consent_lost       — Graph 403
 *   502 federation_graph_unavailable  — Graph 5xx / network / unknown
 *
 * @param {number} clientId
 * @returns {Promise<{
 *   groups: Array<{
 *     id: string,
 *     displayName: string,
 *     alreadyMapped: boolean,
 *     mappedRoles: Array<'manager'|'admin'|'carer'>,
 *   }>,
 *   hasMore: boolean,
 *   totalReturned: number,
 * }>}
 */
export async function listAvailableGroups(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/available-groups`);
}

// ── CarePeople module-role mappings (2b-13 Gate 5b) ─────────
// AD group -> CarePeople module role (hr/supervisor) + tier. Independent
// of core GroupMappings (separate Gate-1 table). Federation JIT/reconcile
// reads these to auto-grant ClientUserCarePeopleRoles.

/**
 * List active CarePeople role mappings for a Client.
 * @returns {Promise<{ mappings: Array<{ mappingId, clientId, groupObjectId,
 *   groupDisplayName, carePeopleRole, roleScopeTier, isActive, createdAt, createdBy }> }>}
 */
export async function listCarePeopleRoleMappings(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/carepeople-role-mappings`);
}

/**
 * Create a CarePeople role mapping.
 * Server rejects supervisor+tier2 (400 invalid_tier_for_role) and a second
 * active mapping for the same group (409 carepeople_role_mapping_already_exists).
 * @param {number} clientId
 * @param {Object} body
 * @param {string} body.groupObjectId
 * @param {'hr'|'supervisor'} body.carePeopleRole
 * @param {0|1|2} body.roleScopeTier
 * @param {string} [body.groupDisplayName]
 */
export async function createCarePeopleRoleMapping(clientId, body) {
  return apiPost(`/mgmt/admin/clients/${clientId}/carepeople-role-mappings`, body);
}

/**
 * Soft-delete a CarePeople role mapping. Idempotent (404 on re-delete).
 * @param {number} clientId
 * @param {number} mappingId
 */
export async function deleteCarePeopleRoleMapping(clientId, mappingId) {
  return apiDelete(`/mgmt/admin/clients/${clientId}/carepeople-role-mappings/${mappingId}`);
}

// ── CareCharge module-role mappings (role-model expansion Push 1, 2026-07-13) ──
// AD group -> CareCharge role (finance | finance_manager). No tier (F1).
// Clone of the CarePeople trio against /carecharge-role-mappings.

/**
 * List active CareCharge role mappings for a Client.
 * @returns {Promise<{ mappings: Array<{ mappingId, clientId, groupObjectId,
 *   groupDisplayName, careChargeRole, isActive, createdAt, createdBy }> }>}
 */
export async function listCareChargeRoleMappings(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/carecharge-role-mappings`);
}

/**
 * Create a CareCharge role mapping.
 * @param {number} clientId
 * @param {Object} body
 * @param {string} body.groupObjectId
 * @param {'finance'|'finance_manager'} body.careChargeRole
 * @param {string} [body.groupDisplayName]
 */
export async function createCareChargeRoleMapping(clientId, body) {
  return apiPost(`/mgmt/admin/clients/${clientId}/carecharge-role-mappings`, body);
}

/**
 * Soft-delete a CareCharge role mapping. Idempotent (404 on re-delete).
 * @param {number} clientId
 * @param {number} mappingId
 */
export async function deleteCareChargeRoleMapping(clientId, mappingId) {
  return apiDelete(`/mgmt/admin/clients/${clientId}/carecharge-role-mappings/${mappingId}`);
}

// ── CarePlan module-role mappings (role-model expansion Push 2, 2026-07-13) ──
// AD group -> CarePlan care_coordinator. Single value, no tier.
export async function listCarePlanRoleMappings(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/careplan-role-mappings`);
}
export async function createCarePlanRoleMapping(clientId, body) {
  return apiPost(`/mgmt/admin/clients/${clientId}/careplan-role-mappings`, body);
}
export async function deleteCarePlanRoleMapping(clientId, mappingId) {
  return apiDelete(`/mgmt/admin/clients/${clientId}/careplan-role-mappings/${mappingId}`);
}

// ── CareRota module-role mappings (CareRota role model, 2026-07-14) ──
// AD group -> CareRota role (rota_coordinator | rota_manager). No tier.
// Clone of the CareCharge trio against /carerota-role-mappings.

/**
 * List active CareRota role mappings for a Client.
 * @returns {Promise<{ mappings: Array<{ mappingId, clientId, groupObjectId,
 *   groupDisplayName, careRotaRole, isActive, createdAt, createdBy }> }>}
 */
export async function listCareRotaRoleMappings(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/carerota-role-mappings`);
}

/**
 * Create a CareRota role mapping.
 * @param {number} clientId
 * @param {Object} body
 * @param {string} body.groupObjectId
 * @param {'rota_coordinator'|'rota_manager'} body.careRotaRole
 * @param {string} [body.groupDisplayName]
 */
export async function createCareRotaRoleMapping(clientId, body) {
  return apiPost(`/mgmt/admin/clients/${clientId}/carerota-role-mappings`, body);
}

/**
 * Soft-delete a CareRota role mapping. Idempotent (404 on re-delete).
 * @param {number} clientId
 * @param {number} mappingId
 */
export async function deleteCareRotaRoleMapping(clientId, mappingId) {
  return apiDelete(`/mgmt/admin/clients/${clientId}/carerota-role-mappings/${mappingId}`);
}
